/**
 * Read the actual server `detail` from an axios error when the request
 * was made with `responseType: "blob"`.
 *
 * Background — the FORF/CCL download bug surfaced 30Apr2026:
 *   axios honours `responseType: "blob"` for ERROR responses too. So
 *   `e.response.data` is a `Blob` instance, not parsed JSON, and
 *   `e.response.data.detail` is `undefined`. The user then sees the
 *   bare axios message ("Request failed with status code 422") instead
 *   of the server's actual `detail` (e.g. the structured 400 with the
 *   `ccl_template_missing_for_matter_type` code).
 *
 * Trust-context: this helper NEVER swallows the underlying error. If
 * we cannot extract a server detail (no `e.response`, blob unreadable,
 * non-JSON body), we fall back to whatever non-empty signal exists in
 * this order: a plain-text blob body, a parsed JSON `detail.message`,
 * the parsed JSON itself stringified, or finally `e.message`. The
 * caller always gets *something* identifying.
 *
 * Returns a single human-readable string suitable for the existing
 * `alert("Download failed: " + msg)` call sites.
 */
export async function parseBlobError(e) {
  // No response object — not an HTTP error. Fall through to the JS error.
  const data = e?.response?.data;
  if (data === undefined || data === null) {
    return e?.message || "Unknown error";
  }

  // Most error sites in this codebase use `responseType: "blob"`. The
  // first branch handles that.
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    let text = "";
    try {
      text = await data.text();
    } catch {
      return e?.message || "Unknown error";
    }
    if (!text) return e?.message || "Unknown error";
    // Try JSON first (FastAPI default error shape: {detail: ...} with
    // detail being either a string or our structured object).
    try {
      const parsed = JSON.parse(text);
      const detail = parsed?.detail;
      if (typeof detail === "string" && detail) return detail;
      if (detail && typeof detail === "object") {
        // Our structured 400 from pipeline._check_ccl_template_mapped
        // carries detail.message — prefer that over JSON.stringify.
        if (typeof detail.message === "string" && detail.message) {
          return detail.message;
        }
        return JSON.stringify(detail);
      }
      // No `detail` field — return whatever the body said.
      if (typeof parsed === "string" && parsed) return parsed;
      return text;
    } catch {
      // Not JSON — return the raw text body. Better than an axios
      // "Request failed" because at least the user sees the actual
      // server message.
      return text;
    }
  }

  // Non-blob (already parsed) error response. FastAPI returns
  // `{detail: ...}`; surface either the string detail or our
  // structured detail.message.
  if (data?.detail) {
    if (typeof data.detail === "string") return data.detail;
    if (typeof data.detail === "object" && typeof data.detail.message === "string") {
      return data.detail.message;
    }
    try {
      return JSON.stringify(data.detail);
    } catch {
      // fall through
    }
  }
  if (typeof data === "string" && data) return data;
  return e?.message || "Unknown error";
}
